import { isApiError } from '@/shared/api';

/**
 * Wat er misging bij inloggen, wisselen, herstellen of accepteren - in de
 * termen van de schermen, niet van HTTP.
 *
 * De API geeft elke fout een eigen problem-`type` (`/problems/<naam>`); het
 * domein ervoor verschilt per omgeving, dus we kijken alleen naar de naam.
 */
export type AuthProblem =
    /** 401: e-mail en wachtwoord horen niet bij elkaar (of het account is geblokkeerd). */
    | 'invalid-credentials'
    /** 429: te veel pogingen; wacht `retryAfterMs`. */
    | 'rate-limited'
    /** 401: dit apparaat is ingetrokken (`reason: revoked`), niet meer vertrouwd of onbekend. */
    | 'device-unavailable'
    /** 403: geen actief lidmaatschap van die omgeving. */
    | 'tenant-access-denied'
    /** 401 op een verzoek met token: de sessie is verlopen. */
    | 'unauthenticated'
    /** 422 met veldfouten. */
    | 'validation'
    | 'invalid-reset-token'
    /** 404: deze uitnodiging bestaat niet (meer). */
    | 'not-found'
    /** 410 */
    | 'invitation-expired'
    /** 409 */
    | 'invitation-already-accepted'
    /** Geen verbinding, of geen antwoord op tijd. */
    | 'offline'
    | 'unknown';

const BY_TYPE: Record<string, AuthProblem> = {
    'invalid-credentials': 'invalid-credentials',
    'rate-limited': 'rate-limited',
    'device-unavailable': 'device-unavailable',
    'tenant-access-denied': 'tenant-access-denied',
    unauthenticated: 'unauthenticated',
    'validation-failed': 'validation',
    validation: 'validation',
    'invalid-reset-token': 'invalid-reset-token',
    'not-found': 'not-found',
    'invitation-expired': 'invitation-expired',
    'invitation-already-accepted': 'invitation-already-accepted',
};

/**
 * Is dit een `device-unavailable` omdat het apparaat is **ingetrokken**?
 *
 * Dat is het enige geval waarin opnieuw inloggen op dit apparaat niet helpt: het
 * moet eerst weer worden toegelaten, op een ander apparaat of door een beheerder.
 * Bij `untrusted` en `unknown` werkt opnieuw inloggen gewoon.
 */
export function isRevokedDevice(error: unknown): boolean {
    return (
        authProblem(error) === 'device-unavailable' &&
        isApiError(error) &&
        error.problem.reason === 'revoked'
    );
}

export function authProblem(error: unknown): AuthProblem {
    if (!isApiError(error)) {
        return 'unknown';
    }

    if (error.kind === 'offline' || error.kind === 'network' || error.kind === 'timeout') {
        return 'offline';
    }

    const name = error.problem.type.split('/problems/')[1];
    const known = name === undefined ? undefined : BY_TYPE[name];

    if (known) {
        return known;
    }

    switch (error.status) {
        case 401:
            return 'unauthenticated';
        case 404:
            return 'not-found';
        case 410:
            return 'invitation-expired';
        case 422:
            return 'validation';
        case 429:
            return 'rate-limited';
        default:
            return 'unknown';
    }
}
